import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getWhatsAppLink } from '../../utils/whatsapp';
type CategoryKey = 'presencia' | 'automatizacion' | 'completa' | 'voice';
interface Tier {
  name: string;
  desc: string;
  setup: string;
  monthly: string;
  featured?: boolean;
  badge?: string;
  capabilities: string[];
  whatsapp: string;
  cta: string;
}
const automatizacion: Tier[] = [{
  name: 'START',
  desc: 'Para comenzar a automatizar.',
  setup: '$1.200.000',
  monthly: '$300.000/mes',
  capabilities: ['WhatsApp + IA', 'Captura de leads', 'Respuestas automáticas', 'CRM básico', 'Transferencia humana'],
  whatsapp: 'Hola ORIGEN, quiero START.',
  cta: 'Quiero START'
}, {
  name: 'GROWTH',
  badge: 'ORIGEN / RECOMENDADO',
  desc: 'Para convertir la atención en un proceso comercial.',
  setup: '$2.500.000',
  monthly: '$650.000/mes',
  featured: true,
  capabilities: ['IA + WhatsApp', 'Calificación avanzada', 'CRM', 'Agenda', 'Seguimiento y reactivación'],
  whatsapp: 'Hola ORIGEN, quiero GROWTH.',
  cta: 'Quiero GROWTH'
}, {
  name: 'PRO',
  desc: 'Para empresas con procesos más complejos.',
  setup: '$4.500.000',
  monthly: '$1.200.000/mes',
  capabilities: ['Automatizaciones avanzadas', 'Integraciones', 'Multi-canal', 'Flujos personalizados', 'Dashboards avanzados'],
  whatsapp: 'Hola ORIGEN, quiero PRO.',
  cta: 'Quiero PRO'
}];
const completa: Tier[] = [{
  name: 'START',
  desc: 'Presencia digital + automatización base.',
  setup: '$4.500.000',
  monthly: '$500.000/mes',
  capabilities: ['Presencia digital nueva', 'Automatización START', 'CRM básico'],
  whatsapp: 'Hola ORIGEN, quiero el Sistema Completo START.',
  cta: 'Quiero este sistema'
}, {
  name: 'GROWTH',
  badge: 'ORIGEN / RECOMENDADO',
  desc: 'Presencia digital + automatización completa.',
  setup: '$6.000.000',
  monthly: '$900.000/mes',
  featured: true,
  capabilities: ['Presencia digital nueva', 'Automatización GROWTH', 'Agenda + seguimiento'],
  whatsapp: 'Hola ORIGEN, quiero el Sistema Completo GROWTH.',
  cta: 'Quiero este sistema'
}, {
  name: 'PRO',
  desc: 'Sistema completo + integraciones avanzadas.',
  setup: '$8.000.000',
  monthly: '$1.450.000/mes',
  capabilities: ['Presencia digital nueva', 'Automatización PRO', 'Integraciones a medida'],
  whatsapp: 'Hola ORIGEN, quiero el Sistema Completo PRO.',
  cta: 'Quiero este sistema'
}];
const voice: Tier[] = [{
  name: 'CORE',
  desc: 'Para empresas que necesitan atención telefónica automatizada.',
  setup: '$2.500.000',
  monthly: '$1.500.000/mes',
  capabilities: ['Llamadas entrantes', 'Identificación y comprensión', 'Captura y calificación', 'CRM + agenda', 'Transferencia humana'],
  whatsapp: 'Hola ORIGEN, quiero VOICE CORE.',
  cta: 'Quiero VOICE CORE'
}, {
  name: 'GROWTH',
  badge: 'ORIGEN / RECOMENDADO',
  desc: 'Para empresas que quieren convertir el teléfono en un canal comercial.',
  setup: '$4.000.000',
  monthly: '$2.800.000/mes',
  featured: true,
  capabilities: ['Todo CORE', 'Llamadas salientes', 'Confirmaciones y reactivación', 'CRM avanzado + WhatsApp', 'Dashboard de seguimiento'],
  whatsapp: 'Hola ORIGEN, quiero VOICE GROWTH.',
  cta: 'Quiero VOICE GROWTH'
}, {
  name: 'SCALE',
  desc: 'Para operaciones de mayor volumen y complejidad.',
  setup: '$6.000.000',
  monthly: '$4.800.000/mes',
  capabilities: ['Todo GROWTH', 'Múltiples agentes y líneas', 'Integraciones vía API', 'Campañas de alto volumen', 'Soporte prioritario'],
  whatsapp: 'Hola ORIGEN, quiero VOICE SCALE.',
  cta: 'Quiero VOICE SCALE'
}];
const categories: {
  key: CategoryKey;
  label: string;
}[] = [{
  key: 'presencia',
  label: 'PRESENCIA DIGITAL'
}, {
  key: 'automatizacion',
  label: 'AUTOMATIZACIÓN'
}, {
  key: 'completa',
  label: 'PRESENCIA DIGITAL COMPLETA'
}, {
  key: 'voice',
  label: 'VOICE'
}];
function TierCard({
  tier
}: {
  tier: Tier;
}) {
  return <div className={`relative flex flex-col p-7 md:p-8 transition-colors duration-200 border ${tier.featured ? 'bg-carbon text-arena border-carbon' : 'bg-arena text-carbon border-carbon/10 hover:border-verde/40'}`} data-id="element-180" data-mp="nxalb5zqr9k0">
      {tier.badge && <span className={`absolute top-0 left-7 -translate-y-1/2 font-body text-[10px] font-medium uppercase tracking-[0.1em] px-2 whitespace-nowrap ${tier.featured ? 'bg-carbon text-verde' : 'bg-arena text-verde'}`} data-id="element-181" data-mp="9xrjsw1fqwgo">
          {tier.badge}
        </span>}
      <h4 className={`font-display text-[18px] font-medium mb-2 ${tier.featured ? 'text-arena' : 'text-carbon'}`} data-id="element-182" data-mp="1zczc68g48sq3">
        {tier.name}
      </h4>
      <p className={`font-body text-[13px] leading-[1.6] mb-6 flex-grow ${tier.featured ? 'text-arena/80' : 'text-carbon/60'}`} data-id="element-183" data-mp="27sf59nnognfh">
        {tier.desc}
      </p>
      <div className={`h-px w-full mb-5 ${tier.featured ? 'bg-arena/20' : 'bg-carbon/10'}`} data-id="element-184" data-mp="2go4njnttx1on" />
      <div className="mb-6" data-id="element-185" data-mp="2lkl8ya9n91y1">
        <span className={`font-display text-[20px] font-medium ${tier.featured ? 'text-arena' : 'text-carbon'}`} data-id="element-186" data-mp="2lo0nnbzeo8yx">
          {tier.setup}
        </span>
        <span className={`font-body text-[12px] ml-1.5 ${tier.featured ? 'text-arena/60' : 'text-carbon/45'}`} data-id="element-187" data-mp="1sy0hzax2yrv0">
          implementación
        </span>
        <div data-id="element-188" data-mp="22veu4ybz0uhi">
          <span className={`font-body text-[14px] font-medium ${tier.featured ? 'text-arena/85' : 'text-carbon/70'}`} data-id="element-189" data-mp="22vy5fw748kmk">
            {tier.monthly}
          </span>
        </div>
      </div>
      <ul className="flex flex-col gap-1.5 mb-6" data-id="element-190" data-mp="3e9sgtzuzn3vy">
        {tier.capabilities.map(cap => <li key={cap} className={`font-body text-[12.5px] flex items-start gap-1.5 ${tier.featured ? 'text-arena/75' : 'text-carbon/60'}`} data-id="element-191" data-mp="2oln2v6shzvka">
            <span className={`mt-[7px] w-1 h-1 rounded-full flex-shrink-0 ${tier.featured ? 'bg-arena/50' : 'bg-verde/50'}`} data-id="element-192" data-mp="2ykc9u4iuvvlr" />
            {cap}
          </li>)}
      </ul>
      <a href={getWhatsAppLink(tier.whatsapp)} target="_blank" rel="noopener noreferrer" className={`mt-auto inline-flex items-center justify-center font-display font-medium text-[14px] px-6 py-3.5 rounded-[6px] transition-colors duration-200 ${tier.featured ? 'bg-arena text-carbon hover:bg-[#EAE7E0]' : 'bg-verde text-arena hover:bg-verde-hover'}`} data-id="element-193" data-mp="25vot8m91xuej">
        {tier.cta} <ArrowRight size={15} className="ml-1.5" data-id="element-194" data-mp="1n3w2yzldanai" />
      </a>
    </div>;
}
export function Pricing() {
  const [active, setActive] = useState<CategoryKey>('automatizacion');
  return <section id="precios" className="bg-arena py-[100px] md:py-[150px]" data-id="element-195" data-mp="3tsxqgs34psah">
      <div className="max-w-[1200px] mx-auto px-6 md:px-20" data-id="element-196" data-mp="2fjx64jb58p6">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="mb-14 text-center max-w-[560px] mx-auto" data-id="element-197" data-mp="2guylxpa64d4">
          <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-[0.15em] mb-4" data-id="element-198" data-mp="gfmcpk457q19">
            PRECIOS
          </span>
          <h2 className="font-display text-[28px] md:text-[38px] font-medium text-carbon leading-[1.15]" data-id="element-199" data-mp="7ewgwgevtznj">
            Lo que puedes contratar y cuánto cuesta.
          </h2>
        </motion.div>

        {/* Category tabs */}
        <div className="flex items-center justify-center gap-2 mb-16 flex-wrap px-2 -mx-2" data-id="element-200" data-mp="2s3kl2q2hf0z1">
          {categories.map(cat => <button key={cat.key} onClick={() => setActive(cat.key)} className={`font-body text-[12.5px] font-medium uppercase tracking-[0.06em] px-4 py-2.5 rounded-[6px] border transition-colors duration-200 ${active === cat.key ? 'bg-carbon text-arena border-carbon' : 'bg-transparent text-carbon/55 border-carbon/15 hover:text-carbon hover:border-carbon/30'}`} data-id="element-201" data-mp="2w3uqa0ig9ncu">
              {cat.label}
            </button>)}
        </div>

        <AnimatePresence mode="wait" data-id="element-202" data-mp="6ebr91bhozd8">
          {active === 'presencia' && <motion.div key="presencia" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="max-w-[640px] mx-auto border border-carbon/10 p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-203" data-mp="1linogrrbx2q">
              <div data-id="element-204" data-mp="wociyllynyst">
                <h4 className="font-display text-[19px] font-medium text-carbon mb-2" data-id="element-205" data-mp="wovqd8dzj4sl">
                  Presencia Digital
                </h4>
                <p className="font-body text-[13.5px] text-carbon/60 leading-[1.6] max-w-[380px]" data-id="element-206" data-mp="16lgu7qe9q9uu">
                  Webs y experiencias digitales diseñadas para representar tu
                  empresa y convertir visitantes en oportunidades.
                </p>
              </div>
              <div className="flex flex-col items-start md:items-end flex-shrink-0" data-id="element-207" data-mp="1kl02qkazrn7o">
                <span className="font-display text-[24px] font-medium text-carbon" data-id="element-208" data-mp="1kix63hfc2vtz">
                  Desde $3.500.000
                </span>
                <span className="font-body text-[12px] text-carbon/45 mb-4" data-id="element-209" data-mp="1ub2tseyjyr3d">
                  Mantenimiento/evolución opcional desde $250.000/mes
                </span>
                <a href={getWhatsAppLink('Hola, quiero hablar con ORIGEN sobre Presencia Digital.')} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-verde text-arena font-display font-medium text-[14px] px-6 py-3.5 rounded-[6px] hover:bg-verde-hover transition-colors duration-200" data-id="element-210" data-mp="23bhhfkyzoia1">
                  Ver presencia digital <ArrowRight size={14} className="ml-1.5" data-id="element-211" data-mp="3pd8nzxtilxg" />
                </a>
              </div>
            </motion.div>}

          {active === 'automatizacion' && <motion.div key="automatizacion" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-id="element-212" data-mp="cmdupzxf07xq">
              {automatizacion.map(tier => <TierCard key={tier.name} tier={tier} data-id="element-213" data-mp="2oe17du2nev1k" />)}
            </motion.div>}

          {active === 'completa' && <motion.div key="completa" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} data-id="element-214" data-mp="10rwup0ibwp9t">
              <p className="text-center font-body text-[13px] text-carbon/45 mb-6 max-w-[480px] mx-auto" data-id="element-215" data-mp="rovpr357947q">
                Presencia digital + automatización en una sola implementación.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-id="element-216" data-mp="1kdjdd5df85og">
                {completa.map(tier => <TierCard key={tier.name} tier={tier} data-id="element-217" data-mp="1ahq4bvywuubf" />)}
              </div>
            </motion.div>}

          {active === 'voice' && <motion.div key="voice" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} data-id="element-218" data-mp="3zb60i1822xe">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mb-6" data-id="element-219" data-mp="5hi3imkbno7t">
                {voice.map(tier => <TierCard key={tier.name} tier={tier} data-id="element-220" data-mp="3nqac4t9cz8d8" />)}
              </div>
              <p className="text-center font-body text-[12.5px] text-carbon/45 max-w-[520px] mx-auto leading-[1.6]" data-id="element-221" data-mp="3slfyft6oap1u">
                Los planes incluyen una capacidad mensual de uso. El consumo
                adicional se cotiza según volumen. No ofrecemos uso ilimitado.
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}